import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "", org_type: "llc" });
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const nav = useNavigate();

  const change = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8 || !/\d/.test(form.password)) {
      toast.error("Password must be 8+ characters and include a digit");
      return;
    }
    setLoading(true);
    try {
      await signup(form);
      toast.success("Account created!");
      nav("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-md mx-auto px-5 lg:px-8 py-16">
        <div className="overline mb-3">START FREE</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mb-2">Create your account</h1>
        <p className="text-neutral-600 mb-8">Browse grants free. Every new account gets <span className="font-semibold text-neutral-900">14 days of full Pro access — no card required</span>.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
          <div>
            <label className="text-sm font-medium">Your name</label>
            <input required value={form.name} onChange={(e) => change("name", e.target.value)}
              className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
              placeholder="Alex Rivera" data-testid="signup-name" />
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <input required type="email" value={form.email} onChange={(e) => change("email", e.target.value)}
              className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
              placeholder="you@org.com" data-testid="signup-email" autoComplete="email" />
          </div>
          <div>
            <label className="text-sm font-medium">Password</label>
            <input required type="password" value={form.password} onChange={(e) => change("password", e.target.value)}
              className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
              placeholder="Min 8 chars, 1 digit" data-testid="signup-password" autoComplete="new-password" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Organization</label>
              <input value={form.org_name} onChange={(e) => change("org_name", e.target.value)}
                className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                placeholder="Acme LLC" data-testid="signup-org" />
            </div>
            <div>
              <label className="text-sm font-medium">Type</label>
              <Select value={form.org_type} onValueChange={(v) => change("org_type", v)}>
                <SelectTrigger className="mt-1 h-11" data-testid="signup-org-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="llc">LLC</SelectItem>
                  <SelectItem value="nonprofit">Nonprofit</SelectItem>
                  <SelectItem value="sole_prop">Sole Prop</SelectItem>
                  <SelectItem value="corp">Corporation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <button disabled={loading} className="btn-primary w-full justify-center" data-testid="signup-submit">
            {loading && <Loader2 size={16} className="animate-spin" />} Create account
          </button>
          <p className="text-xs text-neutral-500 text-center">
            By creating an account you agree to our terms. We never share your data.
          </p>
        </form>
        <div className="mt-6 text-sm text-neutral-600">
          Already have an account? <Link to="/login" className="underline font-medium" data-testid="signup-to-login">Log in</Link>
        </div>
      </div>
    </div>
  );
}
